import * as _ from "underscore";

import EventedModel from "models/EventedModel";
import * as readerReviewUtils from "readerReviewUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";

export default class BookModel extends EventedModel {
  // @ts-ignore
  get urlRoot() {
    return "/api/book";
  }

  // @ts-ignore
  get idAttribute() {
    return "_id";
  }

  defaults() {
    return {
      audioMap: [
        { t: 0, w: 0 },
        { t: 312, w: 498 },
      ],
    };
  }

  async fetchField(field) {
    const { data } = await readlangClient.request(
      "GET",
      "/api/book/" + this.id + "/" + field,
      {}
    );

    this.set(field, data);
    this._revertAttributes[field] = data;
  }

  addTag(tagName, callback?, hide?) {
    readlangClient.request(
      "POST",
      "/api/book/" + this.id + "/tag",
      { tagName: tagName },
      (data) => {
        const userBook = _.clone(this.get("userBook")) ?? { bookID: this.id };
        userBook.tags = data.tags;
        const changes: {
          userBook: { [key: string]: any };
          public?: boolean;
          hide?: boolean;
        } = {
          userBook: userBook,
        };
        if (this.isUserTheOwner()) {
          if (tagName === "shared") {
            changes.public = true;
          }
          if (hide !== undefined) {
            changes.hide = hide;
          }
          this.save(changes, { patch: true });
        } else {
          // Trigger change event without saving to the server
          this.set(changes);
        }
        if (callback) {
          callback();
        }
      }
    );
  }

  isUserTheOwner() {
    return (
      this.get("userBook")?.userID &&
      (this.get("userBook").userID === this.get("ownerID") ||
        this.get("userBook").userID === this.get("ownerID")?._id)
    );
  }

  /**
   * Remove a tag from the book.
   *
   * @param {string} tagName - The name of the tag to remove.
   * @param {Function} [callback] - A callback function to call after the tag has been removed.
   */
  removeTag(tagName, callback) {
    readlangClient.request(
      "DELETE",
      "/api/book/" + this.id + "/tag",
      { tagName: tagName },
      async (data) => {
        const userBook = _.clone(this.get("userBook"));
        userBook.tags = data.tags;
        const changes: {
          userBook: { [key: string]: any };
          public?: boolean;
          hide?: boolean;
        } = {
          userBook: userBook,
        };
        if (this.isUserTheOwner() && tagName === "shared") {
          changes.public = false;
          changes.hide = false;
          await this.save(changes, { patch: true });
        } else {
          // This triggers the change event without saving to the server
          await this.set(changes);
        }
        callback?.();
      }
    );
  }

  hasTag(tagName) {
    return _.contains(this.get("userBook")?.tags ?? [], tagName);
  }

  async patchUserBook(userBookPatch) {
    this.set("userBook", _.extend({}, this.get("userBook"), userBookPatch));
    await readlangClient.request(
      "PATCH",
      "/api/book/" + this.id + "/userBook",
      userBookPatch
    );
  }

  async getHtmlFragment(fromPosition, _toPosition, addReviewButton?) {
    let html = "";
    const toPosition = _toPosition + 1;
    let i = -1;
    const chunksToFetch = [];
    const htmlChunks = this.get("htmlChunks");

    _.every(htmlChunks, (chunk) => {
      let fetch = false;
      i++;

      if (chunk.firstPosition > toPosition) {
        // stop iterating, we've passed the desired fragment
        return false;
      }

      if (
        toPosition >= chunk.firstPosition &&
        fromPosition <= chunk.lastPosition
      ) {
        fetch = true;
      }
      if (
        fromPosition <= chunk.lastPosition &&
        toPosition >= chunk.firstPosition
      ) {
        fetch = true;
      }
      if (fetch) {
        chunksToFetch.push({ index: i, sha1: chunk.sha1 });
      }
      return true;
    });

    for (const chunkToFetch of chunksToFetch) {
      const chunk = htmlChunks[chunkToFetch.index];
      /** The position within the chunk to start from. */
      let fromOffset;
      /** The position within the chunk to end at. */
      let toOffset;
      let fetchedChunk;
      i++;

      if (chunk.firstPosition > toPosition) {
        // @ts-ignore
        console.log("stop");
        break;
      }

      const fetchChunk = async (sha1) => {
        if (fetchedChunk) {
          return fetchedChunk;
        }

        try {
          const { data } = await readlangClient.request(
            "GET",
            `/api/chunk/${sha1}`,
            {},
            () => {},
            async ({ status }) => {
              if (status === 404) {
                if (/regenerated/.test(window.location.hash)) {
                  $("#entirePage").text("Can't regenerate again");
                  return;
                }

                $("#entirePage").html("Regenerating HTML...");

                // Regenerate HTML
                await readlangClient.request("GET", `/api/book/${this.id}`, {
                  forceRegenerate: true,
                });

                // Reload page
                window.location.hash = "regenerated";
                window.location.reload();
                return;
              }

              // Unknown error
              $("#entirePage").html("Unknown error fetching book HTML");
            },
            { cache: true, dataType: "text" }
          );

          fetchedChunk = data;
        } catch (error) {
          console.error("ERROR NO CHUNK");
          console.error(error);
        }

        return fetchedChunk;
      };

      fetchedChunk = null;

      if (fromPosition <= chunk.lastPosition) {
        if (fromPosition <= chunk.firstPosition) {
          fromOffset = 0;
        } else {
          const chunkData = await fetchChunk(chunk.sha1);
          // find starting point
          const fromOffsetIndex =
            Math.floor((fromPosition - chunk.firstPosition) / 8) - 1;
          fromOffset = 0;

          if (fromOffsetIndex >= 0) {
            fromOffset = chunk.positionIndexes[fromOffsetIndex];
          }

          fromOffset = chunkData.indexOf("<span data-p=" + fromPosition + ">");
          while (fromOffset > 0 && !/>| /.test(chunkData[fromOffset - 1])) {
            fromOffset--;
          }
        }
      }

      if (
        toPosition < chunk.firstPosition ||
        fromPosition > chunk.lastPosition
      ) {
        // can skip
      } else {
        const chunkData = await fetchChunk(chunk.sha1);

        if (toPosition <= chunk.lastPosition) {
          const toOffsetIndex =
            Math.floor((toPosition - chunk.firstPosition) / 8) - 1;
          toOffset = 0;

          if (toOffsetIndex >= 0) {
            toOffset = chunk.positionIndexes[toOffsetIndex];
          }
          toOffset = chunkData.indexOf("<span data-p=" + toPosition + ">");
        } else {
          // get it all
          toOffset = chunkData.length;
        }
      }
      if (
        fromPosition <= chunk.lastPosition &&
        toPosition >= chunk.firstPosition
      ) {
        const chunkData = await fetchChunk(chunk.sha1);
        if (
          typeof fromOffset !== "undefined" &&
          typeof toOffset !== "undefined"
        ) {
          html += chunkData.substring(fromOffset, toOffset);
        }
      }
    }

    if (addReviewButton) {
      html = await readerReviewUtils.spliceInReviewSessionButtons(
        this.id as string,
        html,
        toPosition,
        this.get("totalWords"),
        this.get("sections")
      );
    }

    return html;
  }

  vote(vote) {
    readlangClient.request(
      "POST",
      "/api/book/" + this.id + "/vote",
      {
        vote: vote,
      },
      (data) => {
        this.set({
          totalVotes: data.totalVotes,
          yourVote: data.yourVote,
        });
      }
    );
  }

  viewJSON(userID, publicMode) {
    const book = this.toJSON();

    if (book.status === "notAvailable") {
      book.shortTitle = book.title = "Un-shared or deleted by owner";
      book.attributionHtml = "N/A";
      return book;
    }

    book.languageString = languageMap.map[book.language].name;
    if (languageMap.map[book.language].frequencies) {
      book.haveFrequencies = true;
    }

    if (!book.title) {
      book.title = "Uh oh... no title!";
    }

    book.titleLowerCase = book.title.toLowerCase();

    if (book.title.length > 80) {
      book.shortTitle = book.title.substring(0, 78) + "...";
      book.titleHint = book.title;
    } else {
      book.shortTitle = book.title;
    }

    book.authorUrlSortKey = "";
    if (book.url) {
      book.urlDomain = book.url.split("//")[1]?.split("/")[0];
      if (book.urlDomain?.length > 30) {
        book.urlDomain = book.urlDomain.substring(0, 28) + "...";
      }

      book.authorUrlSortKey += book.url;
    }
    if (book.url === "") {
      delete book.url;
    }
    if (book.author === "") {
      delete book.author;
    }
    if (book.author) {
      book.authorUrlSortKey += book.author;
    }

    book.importedDate = new Date(parseInt(book._id.substring(0, 8), 16) * 1000);

    if (publicMode && book.firstShareDate) {
      book.daysAgo = Math.round(
        (new Date().getTime() - new Date(book.firstShareDate).getTime()) /
          (24 * 3600 * 1000)
      );
      book.importedDateString = new Date(book.firstShareDate).toUTCString();
    } else {
      book.daysAgo = Math.round(
        (new Date().getTime() - book.importedDate.getTime()) /
          (24 * 3600 * 1000)
      );
      book.importedDateString = book.importedDate.toUTCString();
    }

    if (book.daysAgo === 0) {
      book.importedString = "today";
    } else if (book.daysAgo === 1) {
      book.importedString = "1 day ago";
    } else {
      book.importedString = book.daysAgo + " days ago";
    }

    if (book.userBook) {
      book.inBookshelf = true;
      book.readDate = book.userBook.date;
      if (book.readDate) {
        book.readDate = new Date(book.readDate);
        book.readDateString = book.readDate.toLocaleDateString();
      }
      if (book.totalWords) {
        book.percentage = Math.round(
          // XXX The Math.min() here and the other instances in the code that follows is a hack,
          // should fix the root cause and prevent position > totalWords occurring
          (100 * Math.min(book.userBook.position, book.userBook.totalWords)) /
            book.totalWords
        );
        if ("readTo" in book.userBook) {
          book.readTo = Math.min(
            100,
            parseInt(book.userBook.readTo, 10) === 0
              ? 0
              : Math.round(
                  (100 * parseInt(book.userBook.readTo, 10) + 1) /
                    book.totalWords
                )
          );
        } else {
          book.readTo = Math.min(
            100,
            parseInt(book.userBook.position, 10) === 0
              ? 0
              : Math.round(
                  (100 * parseInt(book.userBook.position, 10) + 1) /
                    book.totalWords
                )
          );
        }
      }
    }

    if (book.url) {
      book.attributionHtml =
        '<span title="' + book.url + '">' + book.urlDomain + "</span>";
    } else if (book.author) {
      book.attributionHtml = '<span title="Author">' + book.author + "</span>";
    }

    if (book.firstVoterID && book.firstVoterUsername) {
      book.ownerID = {
        _id: book.firstVoterID,
        username: book.firstVoterUsername,
      };
    }

    if (book.ownerID && userID === book.ownerID._id) {
      book.userIsOwner = true;
    }

    if (
      book.ownerID &&
      book.ownerID.username &&
      (publicMode || book.userIsOwner)
    ) {
      if (book.attributionHtml) {
        book.attributionHtml += ", via ";
      } else {
        book.attributionHtml = "via ";
      }
      book.attributionHtml +=
        '<span class="mobileOnly">' +
        book.ownerID.username +
        "</span>" +
        '<a class="noMobile" href="/user/' +
        book.ownerID._id +
        '">' +
        book.ownerID.username +
        "</a>";
    }

    if (book.attributionHtml && book.attributionHtml.length > 0) {
      book.attributionExists = true;
    }

    if (book.metrics) {
      book.difficulty = book.metrics.CEFNumber;
      book.metrics.difficultyString = {
        A1: "Beginner",
        A2: "Upper Beginner",
        B1: "Intermediate",
        B2: "Upper Intermediate",
        C1: "Advanced",
        C2: "Very Advanced",
      }[book.metrics.CEF];
    }

    if (book.imageURL) {
      book.imageURL =
        "/api/getImage/" + encodeURIComponent(book.imageURL) + "/100x";
    }

    if ("totalVotes" in book) {
      book.showVotes = true;

      if (book.yourVote > 0) {
        book.upVoted = true;
      } else if (book.yourVote < 0) {
        book.downVoted = true;
      }
    }

    return book;
  }

  // XXX WTF! This is an abomination!
  populateExtraFields(models, publicMode) {
    this.set(this.viewJSON(models, publicMode));
  }
}
